/* Emberhunt storefront helpers. Loaded with defer after Dawn's constants.js, pubsub.js and global.js. */
(() => {
  const routes = window.routes || {};
  const canPublish = () => typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  const canSubscribe = () => typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

  /* ---------- One-click add to cart: any element with data-eh-add="<variant id>" ---------- */
  const showError = (trigger, message) => {
    let el = trigger.parentElement.querySelector('.eh-add-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'eh-add-error form__message';
      el.setAttribute('role', 'alert');
      trigger.insertAdjacentElement('afterend', el);
    }
    el.textContent = message;
  };

  const addToCart = async (variantId, trigger) => {
    const drawer = document.querySelector('cart-drawer');
    const body = { id: Number(variantId), quantity: 1 };
    if (drawer) {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      drawer.setActiveElement?.(trigger);
    }

    // Shopify standard events (Dawn 16): lets analytics pixels see this add like a normal ATC.
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    let deferred = null;
    if (CartLinesUpdateEvent) {
      deferred = CartLinesUpdateEvent.createPromise();
      trigger.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    }

    trigger.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart.');

      if (deferred) {
        fetch(`${routes.cart_url || '/cart'}.js`)
          .then((r) => r.json())
          .then((cart) => deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) }))
          .catch((e) => deferred.reject(e));
      }
      if (canPublish()) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'emberhunt', productVariantId: String(variantId), cartData: data });
      }
      if (drawer) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } else {
        window.location = routes.cart_url || '/cart';
      }
    } catch (error) {
      deferred?.reject(error);
      showError(trigger, error.message);
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-eh-add]');
    if (!trigger || trigger.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    addToCart(trigger.dataset.ehAdd, trigger);
  });

  /* ---------- Cart add-on: dropdowns pick the variant the Add button adds ---------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-eh-addon-option]');
    const addon = select?.closest('[data-eh-addon]');
    if (!addon) return;
    let variants = [];
    try {
      variants = JSON.parse(addon.querySelector('[data-eh-addon-variants]').textContent);
    } catch (e) {
      return;
    }
    const chosen = {};
    addon.querySelectorAll('[data-eh-addon-option]').forEach((s) => {
      chosen[`o${s.dataset.ehAddonOption}`] = s.value;
    });
    const match = variants.find((v) => Object.keys(chosen).every((key) => v[key] === chosen[key]));
    const button = addon.querySelector('[data-eh-add]');
    const price = addon.querySelector('[data-eh-addon-price]');
    if (!button) return;
    if (match && match.available) {
      button.dataset.ehAdd = match.id;
      button.disabled = false;
      button.textContent = 'Add';
      if (price) price.textContent = match.price;
    } else {
      button.disabled = true;
      button.textContent = 'Sold out';
    }
  });

  /* ---------- Bundle upsell: keep the kit variant in step with the selected seat color ---------- */
  document.querySelectorAll('[data-eh-upsell]').forEach((upsell) => {
    const mapEl = upsell.querySelector('[data-eh-bundle-map]');
    const button = upsell.querySelector('[data-eh-add]');
    if (!mapEl || !button || !canSubscribe()) return;
    let map = {};
    try {
      map = JSON.parse(mapEl.textContent);
    } catch (e) {
      return;
    }
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const option = event?.data?.variant?.option1;
      if (option && map[option]) button.dataset.ehAdd = map[option];
    });
  });

  /* ---------- Estimated delivery: today + handling + transit business days (weekends skipped) ---------- */
  const addBusinessDays = (start, days) => {
    const d = new Date(start);
    let left = days;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
    }
    return d;
  };
  document.querySelectorAll('[data-eh-delivery]').forEach((el) => {
    const n = (key) => Number(el.dataset[key]) || 0;
    const today = new Date();
    const first = addBusinessDays(today, n('handlingMin') + n('transitMin'));
    const last = addBusinessDays(today, n('handlingMax') + n('transitMax'));
    const fmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const out = el.querySelector('[data-eh-delivery-range]');
    if (out) out.textContent = `${fmt.format(first)} – ${fmt.format(last)}`;
  });

  /* ---------- Countdown to a fixed deadline (data-eh-countdown = end time in Unix seconds) ---------- */
  document.querySelectorAll('[data-eh-countdown]').forEach((el) => {
    const end = Number(el.dataset.ehCountdown) * 1000;
    const out = el.querySelector('[data-eh-countdown-time]');
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const left = Math.max(0, end - Date.now());
      if (left === 0) {
        el.remove();
        return false;
      }
      const s = Math.floor(left / 1000);
      const d = Math.floor(s / 86400);
      const time = `${pad(Math.floor((s % 86400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
      out.textContent = d > 0 ? `${d}d ${time}` : time;
      return true;
    };
    if (tick()) {
      const timer = setInterval(() => {
        if (!tick()) clearInterval(timer);
      }, 1000);
    }
  });

  /* ---------- Sticky add to cart (mobile) ---------- */
  const bar = document.querySelector('[data-eh-sticky-atc]');
  const mainForm =
    document.querySelector('product-info product-form form[data-type="add-to-cart-form"]') ||
    document.querySelector('[data-eh-buy] product-form form[data-type="add-to-cart-form"]');
  const mainButton = mainForm?.querySelector('[type="submit"]');
  if (bar && mainButton && 'IntersectionObserver' in window) {
    const stickyButton = bar.querySelector('[data-eh-sticky-btn]');
    const stickyPrice = bar.querySelector('[data-eh-sticky-price]');
    const stickyVariant = bar.querySelector('[data-eh-sticky-variant]');
    const stickyImage = bar.querySelector('.eh-sticky-atc__img');
    const showVariant = (variant) => {
      if (!variant) return;
      if (stickyVariant && variant.title && variant.title !== 'Default Title') stickyVariant.textContent = variant.title;
      const src = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
      if (stickyImage && src) {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '96');
        stickyImage.src = url.toString();
      }
    };

    const sync = () => {
      stickyButton.disabled = mainButton.hasAttribute('disabled');
      const label = mainButton.querySelector('span')?.textContent.trim();
      if (label) stickyButton.textContent = label;
      const price = document.querySelector('product-info [id^="price-"] .price');
      if (price && stickyPrice && !stickyPrice.hasAttribute('data-eh-sticky-static')) {
        const node = price.classList.contains('price--on-sale')
          ? price.querySelector('.price__sale .price-item--last')
          : price.querySelector('.price__regular .price-item--regular');
        if (node) stickyPrice.textContent = node.textContent.trim();
      }
    };

    bar.hidden = false;
    new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', scrolledPast);
      bar.toggleAttribute('inert', !scrolledPast);
    }).observe(mainButton);

    stickyButton.addEventListener('click', () => mainButton.click());
    if (canSubscribe()) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        showVariant(event?.data?.variant);
        setTimeout(sync, 0);
      });
    }
    sync();
  }
})();
